import styled from "styled-components";
import Title from "../../components/title";
import { useTranslation } from "react-i18next";
import { Color } from "../../constants/color";
import { ZIndex } from "../../constants/z-index";
import { BreakpointsQuery } from "../../constants/device";
import CustomSearch from "../../components/search";
import {
  DropdownOptions,
  Employee,
  EmployeesTableFilters,
  ImportChatReply,
} from "../../types";
import EmployeeTable from "../../components/employee-registry/table";
import Button from "../../components/button/button";
import { Icons } from "../../constants/icons";
import React, { useCallback, useEffect, useState } from "react";
import {
  avviaImportGuidato,
  getEmployeeByCompanyId,
} from "../../features/employee/utils";
import ImportChat from "../../components/employee-registry/import-chat";
import * as Ariakit from "@ariakit/react";
import {
  BE_BASE_URL,
  BE_TEMPLATE_DOWNLOAD_ENDPOINT,
  BE_IMPORT_EMPLOYEES_ENDPOINT,
  BE_EMPLOYEE_LIMIT_REACHED_ENDPOINT,
} from "../../utils/config";
import {
  Select,
  SelectItem,
  SelectLabel,
  SelectProvider,
} from "../../components/form/select";
import {
  initEmployeeStateFilter,
  initHeadquartersFilterEmployee,
  initJobDescriptionsFilter,
  chiaveMansione,
} from "../../utils/utils";
import { Status } from "../../constants/status";
import ModalEmployeeEdit from "../../components/employee-registry/modal-employee-edit";
import ChooseCourseModal from "../../components/employee-registry/modal-course-edit";
import { axiosInstance } from "../../provider/authProvider";
import axios from "axios";
import { set } from "date-fns";
import { AxiosError } from "axios";
import { Loader } from "../../components/loader";
import { toast, ToastContainer, Bounce } from "react-toastify";
import { avvisoUnico } from "../../features/notifiche/avvisi";
import Suggerimento from "../../components/tooltip/suggerimento";
import { useSolaLettura } from "../../features/user/solaLettura";
import {
  filtraPerNome,
  filtraPerSedeDiLavoro,
} from "../../features/employee/ricerca";
import { Users } from "lucide-react";
import { StatoVuoto } from "../../components/attestati/stato-vuoto";
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 50px;
`;

const DropdownSection = styled.div`
  display: flex;
  column-gap: 0.8rem;
  background-color: ${Color.BLUE};
  padding: 1rem;
  border-radius: 1rem;
  align-items: flex-end;
  svg {
    position: relative;
    top: 1px;
    right: 2px;
  }
  @media ${BreakpointsQuery.tablet} {
    justify-content: center;
    row-gap: 1rem;
  }
`;

const EditButtonsContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding: 0.5rem 0;
  width: 100%;
`;

const ButtonWithTooltip = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const Tooltip = styled.div`
  visibility: hidden;
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${Color.LIGHT_BLUE};
  color: ${Color.WHITE};
  text-align: center;
  padding: 8px 10px;
  border-radius: 6px;
  width: max-content;
  max-width: 200px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: ${ZIndex.TOOLTIP};
  font-size: 0.8rem;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: ${Color.LIGHT_BLUE} transparent transparent transparent;
  }
`;

// L'esito dell'import da Excel: un solo avviso, riscritto a ogni tentativo.
const AVVISO_IMPORT = "anagrafica-import-excel";

const EmployeeRegistry = () => {
  const { t } = useTranslation();
  // In sola lettura (abbonamento disattivato, ODOO 300) l'anagrafica si consulta e il modello
  // si scarica, ma non si aggiungono né si importano dipendenti.
  const solaLettura = useSolaLettura();
  // L'apertura dell'importazione guidata: presente solo mentre la conversazione e' aperta.
  const [importGuidato, setImportGuidato] = useState<ImportChatReply | null>(
    null
  );
  const [employeeList, setEmployeeList] = useState<Employee[]>();
  const [employeeListArray, setEmployeeListArray] = useState<Employee[]>(
    employeeList!
  );
  const [headquarters, setHeadquarters] = useState<Set<DropdownOptions>>();
  const [employeeStates, setEmployeeStates] = useState<Set<DropdownOptions>>();
  const [jobDescription, setJobDescriptions] = useState<Set<DropdownOptions>>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [loading, setLoading] = useState({
    show: true,
    message: "Caricamento in corso...",
  });
  const [isEmployeeLimitReached, setIsEmployeeLimitReached] = useState(false);
  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance({
        url: BE_BASE_URL + BE_TEMPLATE_DOWNLOAD_ENDPOINT, // Il link del tuo endpoint backend
        method: "GET",
        responseType: "blob", // Questo è necessario per ricevere il file binario
      });

      // Creare un link temporaneo per scaricare il file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Dipendenti-Template.xlsx"); // Nome del file da scaricare
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      //console.error("Errore durante il download del template:", error);
    }
  };

  const showEditModal = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  };
  const closeEditModal = () => {
    setIsModalOpen(false);
    initPage();
    checkEmployeeLimitReached(); // Check employee limit after modal closes
  };

  const handleSubmitSuccess = (employee: Employee) => {
    setSelectedEmployee(employee);
    setSuccessModalVisible(true);
  };
  const checkEmployeeLimitReached = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        BE_BASE_URL + BE_EMPLOYEE_LIMIT_REACHED_ENDPOINT
      );
      setIsEmployeeLimitReached(response.data);
    } catch (error) {
      /*
      //console.error(
        "Errore durante il controllo del limite dipendenti:",
        error
      );
      */
      // In caso di errore, presupponiamo che il limite non sia stato raggiunto
      setIsEmployeeLimitReached(false);
    }
  }, []);

  const initPage = useCallback(() => {
    getEmployeeByCompanyId()!.then((response) => {
      setEmployeeList(response);
      setEmployeeListArray(response);
      initEmployeeStateFilter(setEmployeeStates, t);
      initHeadquartersFilterEmployee(response, setHeadquarters, t);
      initJobDescriptionsFilter(response, setJobDescriptions, t);
      setLoading({ show: false, message: "" });
    });
    checkEmployeeLimitReached();
  }, [t, checkEmployeeLimitReached]);

  useEffect(() => {
    initPage();
  }, [initPage]);

  // Inizializza il filtro con il valore intero 1 (assunto) ma mostra la label corretta nel dropdown
  const [filters, setFilters] = React.useState<EmployeesTableFilters>({
    state: 1, // Impostato a 1 che corrisponde ad "Assunto"
    lastNameAndFirstName: "",
    jobDescription: t("book-course-filter-all"),
    headquarter: t("book-course-filter-all"),
  });

  const filterCourse = useCallback(() => {
    if (!employeeList) return;

    let filtered = employeeList;
    if (filters.state !== t("book-course-filter-all")) {
      if (typeof filters.state === "number") {
        // Se è un numero (come all'inizializzazione), filtra direttamente per il valore numerico
        filtered = filtered.filter((el) => el.state === filters.state);
      } else {
        // Se è una stringa (selezionato dal dropdown), trova il valore corrispondente
        const stateEntry = Array.from(Status.entries()).find(
          ([_, statusObj]) => statusObj.label === filters.state
        );

        if (stateEntry) {
          const [stateValue] = stateEntry;
          filtered = filtered.filter((el) => el.state === stateValue);
        }
      }
    }
    // Cognome e nome si cercano in entrambi gli ordini: "matteo pera" trova PERA MATTEO.
    filtered = filtraPerNome(filtered, filters.lastNameAndFirstName);
    if (filters.jobDescription !== t("book-course-filter-all")) {
      // La tendina offre la mansione normalizzata: il confronto normalizza anche il dato,
      // altrimenti chi e' scritto «IMPIEGATO » non verrebbe piu' trovato da «IMPIEGATO».
      filtered = filtered.filter(
        (el) => chiaveMansione(el.jobDescription) === filters.jobDescription
      );
    }
    if (filters.headquarter !== t("book-course-filter-all")) {
      // Come per la mansione: la tendina offre la sede normalizzata, quindi il confronto
      // normalizza anche il dato, e regge la sede assente (vedi `filtraPerSedeDiLavoro`).
      filtered = filtraPerSedeDiLavoro(filtered, filters.headquarter);
    }
    setEmployeeListArray(filtered);
  }, [
    employeeList,
    filters.headquarter,
    filters.jobDescription,
    filters.lastNameAndFirstName,
    filters.state,
    t,
  ]);

  // Imposta il valore iniziale della tendina correttamente
  const selectEmployeeState = Ariakit.useSelectStore({
    value:
      Status.get(filters.state as number)?.label || t("book-course-filter-all"),
    setValue: (value) => {
      if (value === t("book-course-filter-all")) {
        setFilters((prev) => ({ ...prev, state: value }));
      } else {
        // Trova il valore numerico corrispondente alla label selezionata
        const stateEntry = Array.from(Status.entries()).find(
          ([_, statusObj]) => statusObj.label === value
        );
        if (stateEntry) {
          const [stateValue] = stateEntry;
          setFilters((prev) => ({ ...prev, state: stateValue }));
        } else {
          setFilters((prev) => ({ ...prev, state: value }));
        }
      }
    },
  });

  const selectJobDescription = Ariakit.useSelectStore({
    value: filters.jobDescription.toString(),
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, jobDescription: value }));
    },
  });

  const selectHeadquarter = Ariakit.useSelectStore({
    value: filters.headquarter.toString(),
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, headquarter: value }));
    },
  });

  /*
   Caricamento guidato: si consegna il foglio e si apre la conversazione. A differenza di
   handleImportFile qui non entra niente in anagrafica finche' l'operatore non conferma
   dentro l'assistente.
  */
  const handleImportGuidato = async () => {
    const input = document.createElement("input");
    input.type = "file";
    /*
     Il .csv sta qui e non su handleImportFile: l'import guidato riconosce il formato dai byte
     del file e legge entrambi, l'import diretto apre il file come .xlsx e su un CSV risponde
     che non e' un Excel valido. Un'anagrafica esportata da un gestionale paghe esce quasi
     sempre in CSV, e senza questa riga il selettore non la mostra nemmeno.
    */
    input.accept = ".xlsx,.csv";

    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0];
      if (!file) {
        return;
      }
      setLoading({ show: true, message: t("import-chat-in-attesa") });
      const apertura = await avviaImportGuidato(file);
      setLoading({ show: false, message: "" });
      if (apertura) {
        setImportGuidato(apertura);
      }
    };
    input.click();
  };

  const handleImportFile = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx";

    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      if (target && target.files) {
        const file = target.files[0];
        if (file) {
          const formData = new FormData();
          formData.append("file", file);
          setLoading({ show: true, message: "Caricamento..." });
          try {
            const response = await axiosInstance.post(
              BE_BASE_URL + BE_IMPORT_EMPLOYEES_ENDPOINT + "/excel",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
            const responseData = response.data;
            const toastMessage = responseData.errorMessage
              ? responseData.errorMessage
              : "File caricato con successo!";
            //console.log("File caricato con successo:", toastMessage);
            setLoading({ show: false, message: "" });
            await checkEmployeeLimitReached(); // Check employee limit after import
            /*
             L'esito dell'import si legge prima di ricaricare: il reload azzera gli avvisi,
             quindi la pagina riparte quando l'avviso si chiude - da solo o chiuso a mano -
             e non un istante prima.
            */
            avvisoUnico(
              AVVISO_IMPORT,
              responseData.errorMessage ? "error" : "success",
              toastMessage,
              { onClose: () => window.location.reload() }
            );
          } catch (error) {
            if (axios.isAxiosError(error)) {
              // Verifica se esiste error.response.data.message
              const errorMessage = error.response?.data?.message
                ? error.response.data.message
                : "Si è verificato un errore durante il caricamento del file.";
              /*
              //console.error(
                "Errore durante il caricamento del file:",
                errorMessage
              );
              */
              setLoading({ show: false, message: "" });
              avvisoUnico(AVVISO_IMPORT, "error", errorMessage, {
                onClose: () => window.location.reload(),
              });
            } else {
              // Gestisci altri tipi di errore, se necessari
              // console.error("Errore sconosciuto:", error);
              toast.error(
                "Si è verificato un errore sconosciuto durante il caricamento del file.",
                {
                  position: "top-right",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  theme: "colored",
                  transition: Bounce,
                }
              );
              setLoading({ show: false, message: "" });
            }
          }
        }
      }
    };

    input.click();
  };

  useEffect(() => {
    filterCourse();
  }, [filterCourse, filters]);

  const newEmployee: Employee = {
    state: 0,
    firstName: "",
    lastName: "",
    cf: "",
    jobDescription: "",
    workplace: "",
    hireDate: new Date(),
    email: "",
    phone: "",
    description: "",
    birthPlace: "",
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={true}
        theme="colored"
        transition={Bounce}
        limit={3}
      />
      <PageWrapper>
        <Loader show={loading.show}>{loading.message}</Loader>
        <Title text={t("employee-registry-title")} fontSize={"20px"} />
        {employeeList && (
          <DropdownSection>
            {employeeStates && (
              <SelectProvider store={selectEmployeeState}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.1rem",
                  }}
                >
                  <SelectLabel
                    style={{
                      paddingLeft: "0.5rem",
                      fontSize: "16px",
                      color: Color.WHITE,
                      textTransform: "uppercase",
                      fontFamily: "Avenir Next Bold, sans-serif",
                    }}
                  >
                    {t("employee-registry-dropdown-state")}
                  </SelectLabel>
                  <Select
                    style={{
                      minWidth: "200px",
                      borderStyle: "solid",
                      borderWidth: "2px",
                      borderRadius: "20px",
                      borderColor: Color.LIGHT_BLUE,
                      padding: "0",
                      paddingLeft: "14px",
                      height: "30px",
                      fontFamily: "Avenir Next Regular, sans-serif",
                    }}
                    renderValue={(value) =>
                      Array.from(employeeStates).find(
                        (status) => status.value === value
                      )?.label
                    }
                  >
                    {Object.entries(Array.from(employeeStates)).map(
                      ([key, value]) => (
                        <SelectItem
                          key={`status_${key}`}
                          value={value.label}
                          style={{
                            justifyContent: "center",
                          }}
                        >
                          {value.label}
                        </SelectItem>
                      )
                    )}
                  </Select>
                </div>
              </SelectProvider>
            )}
            <CustomSearch
              name={t("reserved-courses-detail-header-name")}
              label={t("reserved-courses-detail-search-placeholder")}
              labelProps={{
                color: Color.WHITE,
                text: t("reserved-courses-detail-header-name"),
                textAlign: "left",
                textTransform: "uppercase",
                fontWeight: "bold",
                width: "260px",
              }}
              width={"260px"}
              onSearch={(e) => {
                setFilters((prev) => ({ ...prev, lastNameAndFirstName: e }));
              }}
            />
            {jobDescription && (
              <SelectProvider store={selectJobDescription}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.1rem",
                    textAlign: "center",
                  }}
                >
                  <SelectLabel
                    style={{
                      paddingLeft: "0.5rem",
                      fontSize: "16px",
                      color: Color.WHITE,
                      textTransform: "uppercase",
                      fontFamily: "Avenir Next bold, sans-serif",
                    }}
                  >
                    {t("reserved-courses-detail-header-job-description")}
                  </SelectLabel>
                  <Select
                    style={{
                      minWidth: "200px",
                      borderStyle: "solid",
                      borderWidth: "2px",
                      borderRadius: "20px",
                      borderColor: Color.LIGHT_BLUE,
                      padding: "0",
                      paddingLeft: "14px",
                      height: "30px",
                      fontFamily: "Avenir Next Regular, sans-serif",
                    }}
                    renderValue={(value) =>
                      Array.from(jobDescription).find(
                        (cat) => cat.value === value
                      )?.label
                    }
                  >
                    {Object.entries(Array.from(jobDescription)).map(
                      ([key, value]) => (
                        <SelectItem
                          key={`jobDescription_${key}`}
                          value={value.value}
                          style={{
                            justifyContent: "center",
                          }}
                        >
                          {value.label}
                        </SelectItem>
                      )
                    )}
                  </Select>
                </div>
              </SelectProvider>
            )}
            {headquarters && (
              <SelectProvider store={selectHeadquarter}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.1rem",
                    textAlign: "center",
                  }}
                >
                  <SelectLabel
                    style={{
                      paddingLeft: "0.5rem",
                      fontSize: "16px",
                      color: Color.WHITE,
                      textTransform: "uppercase",
                      fontFamily: "Avenir Next Bold, sans-serif",
                    }}
                  >
                    {t("book-course-search-headquarter")}
                  </SelectLabel>
                  <Select
                    style={{
                      minWidth: "200px",
                      borderStyle: "solid",
                      borderWidth: "2px",
                      borderRadius: "20px",
                      borderColor: Color.LIGHT_BLUE,
                      padding: "0",
                      paddingLeft: "14px",
                      height: "30px",
                      fontFamily: "Avenir Next Regular, sans-serif",
                      textTransform: "capitalize",
                    }}
                    renderValue={(value) =>
                      Array.from(headquarters).find(
                        (city) => city.value === value
                      )?.label
                    }
                  >
                    {Object.entries(Array.from(headquarters)).map(
                      ([key, value]) => (
                        <SelectItem
                          key={`city_${key}`}
                          value={value.label}
                          style={{
                            justifyContent: "center",
                            textTransform: "capitalize",
                          }}
                        >
                          {value.label}
                        </SelectItem>
                      )
                    )}
                  </Select>
                </div>
              </SelectProvider>
            )}
          </DropdownSection>
        )}
        {/* Durante il caricamento l'elenco e' undefined e parla il Loader. A
            caricamento finito, un elenco filtrato vuoto si spiega in due modi:
            l'anagrafica ha dei dipendenti ma i filtri li escludono tutti (lo
            stato parte da Assunto, quindi basta che siano tutti cessati), oppure
            l'anagrafica e' proprio vuota. */}
        {employeeListArray &&
          (employeeListArray.length === 0 ? (
            <StatoVuoto
              icona={Users}
              titolo={
                employeeList && employeeList.length > 0
                  ? t("attestati-tab-dipendente-vuoto-filtri")
                  : t("employee-registry-vuoto")
              }
              suggerimento={
                employeeList && employeeList.length > 0
                  ? t("employee-registry-vuoto-filtri-suggerimento")
                  : t("employee-registry-vuoto-suggerimento")
              }
            />
          ) : (
            <EmployeeTable
              employees={employeeListArray}
              showEditModal={showEditModal}
            />
          ))}
        <ModalEmployeeEdit
          isVisible={isModalOpen}
          employee={selectedEmployee}
          closeModal={closeEditModal}
          isNewEmployee={!selectedEmployee?.id}
          handleSubmitSuccess={handleSubmitSuccess}
        />
        <ChooseCourseModal
          isVisible={successModalVisible}
          employee={selectedEmployee}
          onClose={() => {
            setSuccessModalVisible(false);
            setSelectedEmployee(null);
          }}
        />
        <EditButtonsContainer>
          <ButtonWithTooltip>
            {/* Al limite dell'abbonamento il perche' lo spiega il tooltip qui sotto. */}
            <Suggerimento
              testo={
                solaLettura
                  ? t("sola-lettura-comando")
                  : isEmployeeLimitReached
                    ? null
                    : t("employee-registry-aiuto-aggiungi")
              }
            >
              <Button
                label={t("employee-registry-button-add-employee")}
                color={Color.GREEN}
                icon={Icons.BUTTONS.PLUS}
                disabled={isEmployeeLimitReached || solaLettura}
                onClick={() => showEditModal(newEmployee)}
              />
            </Suggerimento>
            {isEmployeeLimitReached && (
              <Tooltip className="tooltip">
                {t("employee-limit-reached-message") ||
                  "Limite di dipendenti raggiunto"}
              </Tooltip>
            )}
          </ButtonWithTooltip>
          <Suggerimento
            testo={t(solaLettura ? "sola-lettura-comando" : "employee-registry-aiuto-carica-ai")}
          >
            <Button
              label={t("import-chat-apri")}
              color={Color.BLUE}
              icon={Icons.BUTTONS.UPLOAD}
              onClick={handleImportGuidato}
              disabled={solaLettura}
            />
          </Suggerimento>
          <Suggerimento
            testo={t(solaLettura ? "sola-lettura-comando" : "employee-registry-aiuto-importa-file")}
          >
            <Button
              label={t("employee-registry-button-import-file")}
              color={Color.LIGHT_BLUE}
              icon={Icons.BUTTONS.UPLOAD}
              onClick={handleImportFile}
              disabled={solaLettura}
            />
          </Suggerimento>
          <Suggerimento testo={t("employee-registry-aiuto-scarica-template")}>
            <Button
              label={t("employee-registry-button-download-template")}
              color={Color.LIGHT_BLUE}
              icon={Icons.BUTTONS.DOWNLOAD}
              onClick={handleDownloadTemplate}
            />
          </Suggerimento>
        </EditButtonsContainer>
      </PageWrapper>
      {importGuidato && (
        <ImportChat
          aperta
          apertura={importGuidato}
          onChiudi={() => setImportGuidato(null)}
          onImportata={() => {
            void checkEmployeeLimitReached();
            window.location.reload();
          }}
        />
      )}
    </>
  );
};
export default EmployeeRegistry;
